import { useParams } from 'react-router-dom';

/** Resolve a tenant slug from URL or localStorage; returns null when unavailable. */
const PRIMARY_TENANT_KEY = 'braise_last_tenant';
const LEGACY_TENANT_KEY = 'eataz_last_tenant';
const FALLBACK_STORAGE_KEYS = ['tenantSlug', 'restaurant_subdomain', 'activeTenant'] as const;

export function resolveTenantSlug(fallback: string | null = null): string | null {
  if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
    return fallback;
  }
  try {
    const storage = window.localStorage;
    const isValid = (value: string | null): value is string =>
      typeof value === 'string' && /^[a-z0-9-]+$/i.test(value);

    const primary = storage.getItem(PRIMARY_TENANT_KEY);
    if (isValid(primary)) {
      return primary;
    }

    const legacy = storage.getItem(LEGACY_TENANT_KEY);
    if (isValid(legacy)) {
      storage.setItem(PRIMARY_TENANT_KEY, legacy);
      return legacy;
    }

    for (const key of FALLBACK_STORAGE_KEYS) {
      const value = storage.getItem(key);
      if (isValid(value)) {
        return value;
      }
    }
  } catch (error) {
    if (import.meta.env.DEV) {
      console.debug('[useTenantPath] tenant slug lookup failed; falling back', error);
    }
  }
  return fallback;
}

/** Build tenant-scoped paths like /r/{slug}/sales/new */
export function useTenantPath(): { tenantPath: (rel: string) => string; subdomain: string | null; slug: string | null } {
  const { subdomain } = useParams();
  const storedSlug = resolveTenantSlug(null);
  const slug = subdomain ?? storedSlug ?? null;

  const tenantPath = (rel: string): string => {
    const trimmed = rel.startsWith('/') ? rel.slice(1) : rel;

    if (!slug) {
      return trimmed ? `/${trimmed}` : '/';
    }

    return trimmed ? `/r/${slug}/${trimmed}` : `/r/${slug}`;
  };

  return { tenantPath, subdomain: subdomain ?? null, slug };
}
